/**
 * OrgQuickView Component
 *
 * Self-contained organisation quick-view panel. Used in the Data Preview
 * dialog (and re-usable elsewhere) to show full organisation details for a
 * selected org while keeping the user inside the current screen.
 *
 * Receives:
 *   - orgId         : currently selected organisation id
 *   - year          : current sales year (sticky from parent)
 *   - onYearChange  : called when user changes year
 *   - activeTab     : currently active sub-tab (sticky from parent)
 *   - onTabChange   : called when user switches tab
 *
 * Internally fetches and manages:
 *   - Org full record  (`/organizations/:id`)
 *   - Sales data       (`/organizations/:id/sales/:year`)
 *   - Sales-years list (`/organizations/:id/sales-years`)
 *   - Communications, Logs (lazy on tab switch)
 *
 * Reuses presentational tab components from `components/organization/*`.
 * For heavyweight ops (email send, document gen, workflow create) it falls
 * back to "Open in Organisations →" which navigates to the full page.
 */
import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Building2, ExternalLink, History, MessageSquare, FileText, ClipboardList, Camera, DollarSign, BarChart3, Users, Download } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, authJsonFetch } from '../lib/api';
import { JobFlagsChips } from './JobFlagsChips';
import { useManagedYears } from '../hooks/useManagedYears';
import {
  SalesTab,
  PhotographyTab,
  ProductionTab,
  AccountsTab,
  CommunicationsTab,
  ContactsTab,
  LogsTab,
} from './organization';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { EmailTemplateDialog } from './EmailTemplateDialog';
import { useComposeEmail } from '../context/ComposeEmailContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { formatDate, formatTime } from '../lib/utils';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Years dropdown — merged live from `useManagedYears`. FIXED_YEARS stays
// as a last-resort fallback for the first few ms before the API responds
// so the picker never renders empty.
const FIXED_YEARS = [2024, 2025, 2026, 2027, 2028, 2029, 2030];

export const OrgQuickView = memo(({
  orgId,
  year,
  onYearChange,
  activeTab,
  onTabChange,
  isBarbiePink = false,
  onDirtyChange,
  saveHandlerRef,
}) => {
  const navigate = useNavigate();
  // Slide-in compose drawer — used to warn when the user switches org while
  // a draft is still unsent for the previous one.
  const composeEmail = useComposeEmail();

  // Watch orgId changes. If a compose draft is open for a DIFFERENT org and
  // is dirty, trigger the blocking switch-org confirm dialog (handled by the
  // context). The dialog lets the operator either finish the email (drawer
  // restores) or discard the draft. The org view has already switched —
  // that's fine; the draft carries the original org_name in its header.
  useEffect(() => {
    if (!composeEmail?.open) return;
    if (!composeEmail.originOrgId) return;
    if (composeEmail.originOrgId === orgId) return;
    if (!composeEmail.isDirty) return;
    composeEmail.guardOrgSwitch(orgId);
    // We deliberately depend only on orgId so we don't fire repeatedly while
    // the user keeps typing in the drawer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Org core
  const [viewingOrg, setViewingOrg] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Sales data (drives Sales / Photography / Production / Accounts tabs)
  const [salesData, setSalesData] = useState({});
  const [salesYears, setSalesYears] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesDirty, setSalesDirty] = useState(false);

  // Push dirty-state changes up to the parent so the Data View can
  // prompt before switching to a different org from the top table.
  useEffect(() => {
    onDirtyChange?.(salesDirty);
  }, [salesDirty, onDirtyChange]);

  // Managed-years list — called at the top so it stays above any early
  // returns below (rules-of-hooks).
  const managedYears = useManagedYears();

  // Expose saveSalesData imperatively — the Data View parent calls
  // this from its "Save & Continue" button in the row-switch dialog.
  // The handler is only wired AFTER saveSalesData is defined; see the
  // second effect further down.

  // Generate Letters dialog (admin/standard alike). Mirrors the same flow
  // that lives on the full Organisations page so operators don't have to
  // navigate away just to print a confirmation letter for an org they're
  // already inspecting in the Data Preview.
  const [genLettersOpen, setGenLettersOpen] = useState(false);
  const [genLettersTemplates, setGenLettersTemplates] = useState([]);
  const [genLettersTemplate, setGenLettersTemplate] = useState('preschool_confirmation');
  const [genLettersBusy, setGenLettersBusy] = useState(false);
  const [savingSales, setSavingSales] = useState(false);
  const salesFormRef = useRef({});

  // Unsaved-changes dialog (replaces window.confirm)
  // pendingAction: { kind: 'tab'|'year', value: any }
  const [unsavedDialog, setUnsavedDialog] = useState({ open: false, pendingAction: null });

  // Email template-picker dialog (per-contact "Send Email")
  const [emailDialog, setEmailDialog] = useState({ open: false, contact: null });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);

  /**
   * Fetch email templates.
   *
   * History: this used to run only when the user clicked "Send Email" on a
   * contact, which meant the dialog opened with `templates=[]` and — if the
   * fetch silently failed (auth lapse, CORS, network blip) — showed the
   * unhelpful "No templates available" copy. Operators reported the popup
   * never loaded templates even when the Email Hub showed dozens.
   *
   * Fix: kick off the fetch as soon as OrgQuickView mounts (or the org id
   * changes), in parallel with the rest of the org data. By the time the
   * user clicks "Send Email" the templates are almost always already in
   * memory, and on failure we surface a toast so the issue isn't silent.
   */
  const fetchEmailTemplates = useCallback(async () => {
    setEmailTemplatesLoading(true);
    try {
      const res = await authFetch(`${API}/email-templates`);
      if (res.ok) {
        const data = await res.json();
        setEmailTemplates(Array.isArray(data) ? data : []);
      } else {
        const errText = await res.text().catch(() => '');
        console.error('Failed to fetch email templates:', res.status, errText);
        toast.error(`Couldn't load email templates (${res.status})`);
      }
    } catch (err) {
      console.error('Failed to fetch email templates:', err);
      toast.error("Couldn't load email templates — check your connection");
    } finally {
      setEmailTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmailTemplates();
  }, [fetchEmailTemplates]);

  const openSendEmail = useCallback((contact) => {
    setEmailDialog({ open: true, contact });
    // If the initial fetch failed or returned empty, retry on demand so the
    // user has a chance to recover without reloading the whole page.
    if (!emailTemplates.length && !emailTemplatesLoading) {
      fetchEmailTemplates();
    }
  }, [emailTemplates.length, emailTemplatesLoading, fetchEmailTemplates]);

  // Communications
  const [communications, setCommunications] = useState([]);
  const [communicationsLoading, setCommunicationsLoading] = useState(false);
  const [newCommType, setNewCommType] = useState('phone_call');
  const [newCommNote, setNewCommNote] = useState('');
  const [addingComm, setAddingComm] = useState(false);

  // Logs
  const [orgLogs, setOrgLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);

  // Fetch org core
  const fetchOrg = useCallback(async (id) => {
    if (!id) return;
    setViewLoading(true);
    try {
      const res = await authFetch(`${API}/organizations/${id}`);
      if (res.ok) {
        const data = await res.json();
        setViewingOrg(data);
      } else {
        toast.error('Failed to load organisation');
      }
    } catch (err) {
      console.error('fetchOrg failed:', err);
      toast.error('Failed to load organisation');
    } finally {
      setViewLoading(false);
    }
  }, []);

  // Fetch sales data for the current year
  const fetchSalesData = useCallback(async (id, yr) => {
    if (!id || !yr) return;
    setSalesLoading(true);
    setSalesDirty(false);
    salesFormRef.current = {};
    try {
      const [salesRes, yearsRes] = await Promise.all([
        authFetch(`${API}/organizations/${id}/sales/${yr}`),
        authFetch(`${API}/organizations/${id}/sales-years`),
      ]);
      if (salesRes.ok) {
        setSalesData(await salesRes.json());
      }
      if (yearsRes.ok) {
        const data = await yearsRes.json();
        setSalesYears(data.years || []);
      }
    } catch (err) {
      console.error('fetchSalesData failed:', err);
    } finally {
      setSalesLoading(false);
    }
  }, []);

  const fetchCommunications = useCallback(async (id) => {
    if (!id) return;
    setCommunicationsLoading(true);
    try {
      const res = await authFetch(`${API}/organizations/${id}/communications`);
      if (res.ok) {
        const data = await res.json();
        // Endpoint returns a plain array of communications (not wrapped)
        setCommunications(Array.isArray(data) ? data : (data?.communications || []));
      }
    } catch (err) {
      console.error('fetchCommunications failed:', err);
    } finally {
      setCommunicationsLoading(false);
    }
  }, []);

  const fetchOrgLogs = useCallback(async (id, page = 1) => {
    if (!id) return;
    setLogsLoading(true);
    try {
      const res = await authFetch(`${API}/organizations/${id}/logs?page=${page}&limit=20`);
      if (res.ok) {
        const data = await res.json();
        setOrgLogs(data.data || []);
        setLogsTotalPages(data.total_pages || 1);
        setLogsPage(page);
      }
    } catch (err) {
      console.error('fetchOrgLogs failed:', err);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  // Reload when org changes
  useEffect(() => {
    if (!orgId) {
      setViewingOrg(null);
      setSalesData({});
      setCommunications([]);
      setOrgLogs([]);
      return;
    }
    fetchOrg(orgId);
    fetchSalesData(orgId, year);
    // Lazy: only refetch comm/logs if those tabs are active
    if (activeTab === 'communications') fetchCommunications(orgId);
    if (activeTab === 'logs') fetchOrgLogs(orgId, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Reload sales data when year changes
  useEffect(() => {
    if (orgId) fetchSalesData(orgId, year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  // Sticky tab — load that tab's data when switched
  const handleTabChange = (tab) => {
    // Guard tab switch when sales form has unsaved changes
    if (salesDirty && ['sales', 'photography', 'production', 'accounts'].includes(activeTab)) {
      setUnsavedDialog({ open: true, pendingAction: { kind: 'tab', value: tab } });
      return;
    }
    applyTabChange(tab);
  };

  const applyTabChange = (tab) => {
    onTabChange?.(tab);
    if (!orgId) return;
    if (tab === 'communications') fetchCommunications(orgId);
    if (tab === 'logs') fetchOrgLogs(orgId, 1);
    if (['sales', 'photography', 'production', 'accounts'].includes(tab)) {
      // Sales-driven tabs already have data; no refetch needed
    }
  };

  // Sales form change handler
  const updateSalesFormRef = useCallback((field, value) => {
    salesFormRef.current[field] = value;
    setSalesDirty(true);
  }, []);

  const saveSalesData = async () => {
    if (!orgId) return;
    setSavingSales(true);
    try {
      const dataToSave = { ...salesData, ...salesFormRef.current };
      const res = await authJsonFetch(`${API}/organizations/${orgId}/sales/${year}/bulk`, {
        method: 'PUT',
        body: JSON.stringify({ data: dataToSave }),
      });
      if (res.ok) {
        const data = await res.json();
        setSalesData(data);
        salesFormRef.current = {};
        setSalesDirty(false);
        toast.success('Sales data saved');
        return true;
      } else {
        toast.error('Failed to save');
        return false;
      }
    } finally {
      setSavingSales(false);
    }
  };

  // Register the save handler against the parent-provided ref. Runs on
  // every render but assigning the same function to .current is cheap.
  if (saveHandlerRef) saveHandlerRef.current = saveSalesData;

  // Communications handlers
  const addCommunicationNote = async () => {
    if (!orgId || !newCommNote.trim()) return;
    setAddingComm(true);
    try {
      const res = await authJsonFetch(`${API}/organizations/${orgId}/communications`, {
        method: 'POST',
        body: JSON.stringify({ note_type: newCommType, content: newCommNote }),
      });
      if (res.ok) {
        setNewCommNote('');
        await fetchCommunications(orgId);
        toast.success('Note added');
      } else {
        toast.error('Failed to add note');
      }
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setAddingComm(false);
    }
  };

  const deleteCommunicationNote = async (noteId) => {
    if (!orgId) return;
    if (!window.confirm('Delete this note?')) return;
    try {
      const res = await authFetch(`${API}/organizations/${orgId}/communications/${noteId}`, { method: 'DELETE' });
      if (res.ok) {
        await fetchCommunications(orgId);
        toast.success('Note deleted');
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Lazy-load document templates the first time the user opens the dialog.
  const openGenerateLetters = useCallback(async () => {
    setGenLettersOpen(true);
    if (genLettersTemplates.length > 0) return;
    try {
      const res = await authFetch(`${API}/documents/templates`);
      if (res.ok) {
        const data = await res.json();
        const tpls = data.templates || [];
        setGenLettersTemplates(tpls);
        // Default to first template if none picked
        if (tpls.length > 0 && !tpls.find(t => t.id === genLettersTemplate)) {
          setGenLettersTemplate(tpls[0].id);
        }
      }
    } catch (err) {
      toast.error('Failed to load templates');
    }
  }, [genLettersTemplate, genLettersTemplates.length]);

  // Run the Generate Confirmation Letter flow for the currently-viewed org
  // at the currently-selected year. Mirrors generateDocument() on the full
  // OrganizationsPage — opens Drive link if available, otherwise direct-
  // downloads the .docx blob to the user's browser.
  //
  // ⚠️ Popup-blocker bypass (Jun 19 2026): we open the destination tab
  // SYNCHRONOUSLY inside the click handler's call stack. If we waited
  // until `data.drive_link` was returned, Chrome/Safari would silently
  // block `window.open` because the user-gesture is considered consumed
  // after the first await. Fallback: render an inline "Open document"
  // link in the toast if the popup is blocked.
  const runGenerateLetters = useCallback(async () => {
    if (!orgId || !genLettersTemplate) return;

    const placeholder = window.open('about:blank', '_blank');
    if (placeholder) {
      try {
        placeholder.document.write(
          '<title>Generating…</title>' +
            '<body style="font-family:system-ui,sans-serif;padding:2.5rem;color:#444;background:#fafafa;">' +
            '<h3 style="margin:0 0 8px;">Generating your document…</h3>' +
            '<p style="margin:0;color:#666;">This tab will redirect to Google Drive when ready.</p>' +
            '</body>'
        );
      } catch (_) { /* cross-origin write — ignore */ }
    }
    const closePlaceholder = () => {
      if (placeholder && !placeholder.closed) placeholder.close();
    };

    setGenLettersBusy(true);
    try {
      const token = localStorage.getItem('gs_session_token');
      const res = await fetch(
        `${API}/organizations/${orgId}/download-document?year=${year}&template=${genLettersTemplate}`,
        {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (!res.ok) {
        closePlaceholder();
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to generate document');
        return;
      }
      const data = await res.json();
      if (!data.success) {
        closePlaceholder();
        toast.error('Failed to generate document');
        return;
      }
      if (data.drive_link) {
        if (placeholder && !placeholder.closed) {
          placeholder.location.href = data.drive_link;
          toast.success('Document saved to Google Drive!');
        } else {
          toast.success(
            <span>
              Document saved.{' '}
              <a
                href={data.drive_link}
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-semibold"
                data-testid="generate-letters-open-fallback"
              >
                Open document ↗
              </a>
            </span>,
            { duration: 20000 },
          );
        }
      } else if (data.direct_download && data.file_data) {
        closePlaceholder();
        toast.success('Document generated! Downloading…');
        const byteChars = atob(data.file_data);
        const byteNums = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNums)], { type: data.file_type });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        closePlaceholder();
        toast.success('Document generated!');
      }
      setGenLettersOpen(false);
    } catch (err) {
      closePlaceholder();
      toast.error('Failed to generate document');
    } finally {
      setGenLettersBusy(false);
    }
  }, [orgId, year, genLettersTemplate]);

  // Empty state
  if (!orgId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
        <Building2 className="w-12 h-12 mb-3 opacity-40" />
        <p className="text-sm font-medium">Select an organisation above</p>
        <p className="text-xs mt-1">Click any row in the preview to see full details here</p>
      </div>
    );
  }

  if (viewLoading || !viewingOrg) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  // Sticky tab default
  const currentTab = activeTab || 'sales';
  const currentYear = String(year);
  const yearOptions = Array.from(new Set([
    ...FIXED_YEARS,
    ...(managedYears.years || []),
    ...salesYears,
  ])).sort();

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header - org name, year selector, open-in-orgs button */}
      <div className="flex items-start justify-between px-4 py-2 border-b bg-muted/30 flex-shrink-0">
        <div className="flex items-start gap-3 min-w-0">
          <Building2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-3 min-w-0 flex-wrap">
              <h3 className="font-semibold truncate" data-testid="quickview-org-name">
                {viewingOrg.name}
              </h3>
              {/* Primary contact summary — inline with location so it stays
              compact. Email is clickable → opens the Email Template picker
              for that contact, mirroring the per-row "send email" flow on
              the full Organisations page. We pick the contact flagged as
              primary; falling back to the first contact in the list. */}
          {(() => {
            const contacts = Array.isArray(viewingOrg.contacts) ? viewingOrg.contacts : [];
            const primary = contacts.find(c => c?.is_primary) || contacts[0];
            if (!primary) return null;
            const bits = [];
            if (primary.name) bits.push(primary.name);
            if (primary.phone) bits.push(primary.phone);
            return (
              <span
                className="text-xs text-muted-foreground flex-shrink-0 truncate max-w-[480px]"
                data-testid="quickview-primary-contact"
                title={`Primary contact: ${primary.name || ''}${primary.phone ? ' · ' + primary.phone : ''}${primary.email ? ' · ' + primary.email : ''}`}
              >
                {bits.length > 0 && <span>· {bits.join(' · ')}</span>}
                {primary.email && (
                  <>
                    {bits.length > 0 ? ' · ' : '· '}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEmailDialog({ open: true, contact: primary });
                      }}
                      className="text-blue-600 hover:underline"
                      data-testid="quickview-primary-contact-email"
                      title="Send email to this contact"
                    >
                      {primary.email}
                    </button>
                  </>
                )}
              </span>
            );
          })()}
            </div>
            {/* Address block — sits directly under the org name in a
                muted, slightly smaller font so it doesn't compete with
                the header. Renders only what's present:
                  line 1: street  (e.g. "35 John Warren Avenue")
                  line 2: city, state, postcode
                Falls back gracefully when partial data is stored. */}
            {(() => {
              const street = (viewingOrg.address || '').trim();
              const locality = [viewingOrg.city, viewingOrg.state, viewingOrg.postcode]
                .map(v => (v || '').toString().trim())
                .filter(Boolean)
                .join(', ');
              if (!street && !locality) return null;
              return (
                <div className="text-[11px] leading-tight text-muted-foreground mt-0.5" data-testid="quickview-org-address">
                  {street && <div className="truncate">{street}</div>}
                  {locality && <div className="truncate">{locality}</div>}
                </div>
              );
            })()}
            {/* Flags row — per (org × selected year). Lets the operator mark
                the current job as needing attention from Marketing, Photography,
                Production and/or Financials with an optional short note. */}
            {orgId && year && (
              <div className="mt-1.5">
                <JobFlagsChips orgId={orgId} year={year} />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={openGenerateLetters}
            data-testid="quickview-generate-letters"
            className="h-7 text-xs"
            title="Generate a Confirmation Letter for this organisation"
          >
            <FileText className="w-3 h-3 mr-1" />
            Generate Letters
          </Button>
          <Label className="text-xs">Year:</Label>
          <Select value={currentYear} onValueChange={(v) => {
            const newYear = Number(v);
            if (salesDirty) {
              setUnsavedDialog({ open: true, pendingAction: { kind: 'year', value: newYear } });
              return;
            }
            onYearChange?.(newYear);
          }}>
            <SelectTrigger className="w-24 h-7 text-xs" data-testid="quickview-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
              {managedYears.isAdmin && (
                <div
                  role="button"
                  tabIndex={0}
                  onMouseDown={(e) => {
                    // Prevent Radix from closing before we show the prompt.
                    e.preventDefault();
                    e.stopPropagation();
                    const input = window.prompt('Add a new year to the picker:', String(new Date().getFullYear() + 1));
                    if (!input) return;
                    managedYears.addYear(parseInt(input, 10));
                  }}
                  className="px-2 py-1.5 text-xs text-primary hover:bg-muted cursor-pointer border-t mt-1 pt-1.5"
                  data-testid="quickview-year-add"
                >
                  + Add year…
                </div>
              )}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/organizations?org=${orgId}`)}
            data-testid="quickview-open-full"
            className="h-7 text-xs"
          >
            <ExternalLink className="w-3 h-3 mr-1" />
            Open Full Page
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <Tabs value={currentTab} onValueChange={handleTabChange} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="flex-shrink-0 mx-4 mt-2 grid grid-cols-7 w-auto">
            <TabsTrigger value="sales" className="text-xs gap-1" data-testid="quickview-tab-sales">
              <DollarSign className="w-3 h-3" /> Sales
            </TabsTrigger>
            <TabsTrigger value="photography" className="text-xs gap-1" data-testid="quickview-tab-photography">
              <Camera className="w-3 h-3" /> Photography
            </TabsTrigger>
            <TabsTrigger value="production" className="text-xs gap-1" data-testid="quickview-tab-production">
              <ClipboardList className="w-3 h-3" /> Production
            </TabsTrigger>
            <TabsTrigger value="accounts" className="text-xs gap-1" data-testid="quickview-tab-accounts">
              <BarChart3 className="w-3 h-3" /> Accounts
            </TabsTrigger>
            <TabsTrigger value="contacts" className="text-xs gap-1" data-testid="quickview-tab-contacts">
              <Users className="w-3 h-3" /> Contacts ({viewingOrg.contacts?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="communications" className="text-xs gap-1" data-testid="quickview-tab-communications">
              <MessageSquare className="w-3 h-3" /> Notes
            </TabsTrigger>
            <TabsTrigger value="logs" className="text-xs gap-1" data-testid="quickview-tab-logs">
              <History className="w-3 h-3" /> Logs
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-auto px-4 py-3">
            <TabsContent value="sales" className="mt-0">
              <SalesTab
                salesYear={year}
                salesData={salesData}
                salesLoading={salesLoading}
                salesDirty={salesDirty}
                savingSales={savingSales}
                saveSalesData={saveSalesData}
                updateSalesFormRef={updateSalesFormRef}
              />
            </TabsContent>

            <TabsContent value="photography" className="mt-0">
              <PhotographyTab
                salesYear={year}
                salesData={salesData}
                salesLoading={salesLoading}
                salesDirty={salesDirty}
                savingSales={savingSales}
                saveSalesData={saveSalesData}
                updateSalesFormRef={updateSalesFormRef}
              />
            </TabsContent>

            <TabsContent value="production" className="mt-0">
              <ProductionTab
                salesYear={year}
                salesData={salesData}
                salesLoading={salesLoading}
                salesDirty={salesDirty}
                savingSales={savingSales}
                saveSalesData={saveSalesData}
                updateSalesFormRef={updateSalesFormRef}
              />
            </TabsContent>

            <TabsContent value="accounts" className="mt-0">
              <AccountsTab
                salesYear={year}
                salesData={salesData}
                salesLoading={salesLoading}
                salesDirty={salesDirty}
                savingSales={savingSales}
                saveSalesData={saveSalesData}
                updateSalesFormRef={updateSalesFormRef}
              />
            </TabsContent>

            <TabsContent value="contacts" className="mt-0">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-muted-foreground">
                  Read-only view of contacts. Use "Open Full Page" to add or edit contacts.
                </p>
              </div>
              <ContactsTab
                contacts={viewingOrg.contacts || []}
                openContactDialog={() => navigate(`/organizations?org=${orgId}`)}
                openEditContactDialog={() => navigate(`/organizations?org=${orgId}`)}
                setAsPrimary={() => navigate(`/organizations?org=${orgId}`)}
                handleDeleteContact={() => navigate(`/organizations?org=${orgId}`)}
                onSendEmailToContact={openSendEmail}
              />
            </TabsContent>

            <TabsContent value="communications" className="mt-0">
              <CommunicationsTab
                communications={communications}
                communicationsLoading={communicationsLoading}
                newCommType={newCommType}
                setNewCommType={setNewCommType}
                newCommNote={newCommNote}
                setNewCommNote={setNewCommNote}
                addingComm={addingComm}
                addCommunicationNote={addCommunicationNote}
                deleteCommunicationNote={deleteCommunicationNote}
                openEmailDialog={() => navigate(`/organizations?org=${orgId}`)}
                viewingOrg={viewingOrg}
                formatDate={formatDate}
                formatTime={formatTime}
                moveImportedNote={() => navigate(`/organizations?org=${orgId}`)}
                allOrgs={[]}
              />
            </TabsContent>

            <TabsContent value="logs" className="mt-0">
              <LogsTab
                orgLogs={orgLogs}
                logsLoading={logsLoading}
                logsPage={logsPage}
                logsTotalPages={logsTotalPages}
                fetchOrgLogs={fetchOrgLogs}
                viewingOrgId={orgId}
                formatDate={formatDate}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* Email template-picker dialog (per-contact send email) */}
      <EmailTemplateDialog
        open={emailDialog.open}
        onOpenChange={(open) => setEmailDialog((prev) => ({ ...prev, open }))}
        org={viewingOrg}
        contact={emailDialog.contact}
        templates={emailTemplates}
        loading={emailTemplatesLoading}
      />

      {/* Generate Confirmation Letter dialog — opened from the Generate
          Letters button above the Year picker. Reuses the same backend
          endpoint as the full Organisations page. */}
      <Dialog open={genLettersOpen} onOpenChange={setGenLettersOpen}>
        <DialogContent className="sm:max-w-md" data-testid="generate-letters-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Generate Confirmation Letter
            </DialogTitle>
            <DialogDescription>
              Pick a template — the letter will be generated for{' '}
              <strong>{viewingOrg?.name || 'this organisation'}</strong> using the data from year{' '}
              <strong>{year}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label className="text-sm">Template</Label>
              <Select value={genLettersTemplate} onValueChange={setGenLettersTemplate}>
                <SelectTrigger data-testid="generate-letters-template-select">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {genLettersTemplates.length === 0 && (
                    <SelectItem value="__loading__" disabled>Loading templates…</SelectItem>
                  )}
                  {genLettersTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name || t.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setGenLettersOpen(false)}
              disabled={genLettersBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={runGenerateLetters}
              disabled={genLettersBusy || !genLettersTemplate || genLettersTemplate === '__loading__'}
              data-testid="generate-letters-submit"
              className={isBarbiePink ? 'bg-pink-500 hover:bg-pink-600' : undefined}
            >
              {genLettersBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Generate & Save to Drive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved-changes dialog (replaces window.confirm) */}
      <UnsavedChangesDialog
        open={unsavedDialog.open}
        onOpenChange={(v) => setUnsavedDialog((prev) => ({ ...prev, open: v }))}
        description={
          unsavedDialog.pendingAction?.kind === 'year'
            ? 'You have unsaved sales changes. What would you like to do before switching year?'
            : 'You have unsaved sales changes. What would you like to do before switching tab?'
        }
        saveLabel="Save & Switch"
        discardLabel="Discard & Switch"
        onSave={saveSalesData}
        onDiscard={() => {
          salesFormRef.current = {};
          setSalesDirty(false);
        }}
        onContinue={() => {
          const action = unsavedDialog.pendingAction;
          if (!action) return;
          if (action.kind === 'tab') {
            applyTabChange(action.value);
          } else if (action.kind === 'year') {
            onYearChange?.(action.value);
          }
          setUnsavedDialog({ open: false, pendingAction: null });
        }}
      />
    </div>
  );
});

OrgQuickView.displayName = 'OrgQuickView';

export default OrgQuickView;
